import { call, put, select, takeLatest } from 'redux-saga/effects';

import { getPromozavrTestIdsFromExperimentBoxes } from '../../../../../common/helpers';
import type { RootState } from '../../../../init-store';
import { createLogger } from '../../helpers/loggerFacade';
import { selectIsYandexNet } from '../config/selectors';
import { selectMultiplePromos } from './api';
import { getPromozavrRendererSource } from './renderer-source';
import { resolvePromozavrInit } from './resolve-promozavr-init';
import { selectShouldUsePaas, selectShouldUsePdokPromoRenderer } from './selectors';
import { failPromozavrRequest, finishPromozavrRequest, initPromozavr, startPromozavrRequest } from './slice';
import type { SelectMultiplePromosModelResponse } from './types';

const logger = createLogger('promozavr');

export function* initPromozavrSaga(action: ReturnType<typeof initPromozavr>) {
    const { payload } = action;
    const state: RootState = yield select();
    const resolvedInit = resolvePromozavrInit(
        {
            ...payload,
            lang: state.config.appLang,
            tld: state.config.tld,
            testIds: getPromozavrTestIdsFromExperimentBoxes(state.config.experiments?.boxes),
            isYandexNet: selectIsYandexNet(state),
            allowDebugParams: state.config.environment === 'development',
            shouldUsePaas: selectShouldUsePaas(state),
            shouldUsePdokPromoRenderer: selectShouldUsePdokPromoRenderer(state),
        },
        getPromozavrRendererSource
    );

    if (!resolvedInit) {
        return;
    }
    const { request, requestKey, rendererSource } = resolvedInit;

    yield put(
        startPromozavrRequest({
            request,
            requestKey,
            rendererSource,
        })
    );

    try {
        const response: SelectMultiplePromosModelResponse = yield call(selectMultiplePromos, request);

        logger.info('selectMultiplePromos resolved', {
            request,
            response,
            rendererSource,
        });

        yield put(finishPromozavrRequest(response));
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);

        logger.error(error, {
            method: 'initPromozavrSaga',
            additional: {
                request,
                requestKey,
                rendererSource,
            },
        });

        yield put(failPromozavrRequest(errorMessage));
    }
}

export default function* promozavrSaga(): Generator {
    yield takeLatest(initPromozavr.type, initPromozavrSaga);
}
